import { oc } from "@orpc/contract";
import { z } from "zod";
import { TranslatedEmailContentSchema } from "./email-threads.contract";

const UUIDSchema = z.uuid();

/**
 * Reprocess Inbound Email Input Schema
 */
export const ReprocessInboundEmailInputSchema = z.object({
  inboundEmailId: UUIDSchema,
});

/**
 * Reprocess Inbound Email Output Schema
 */
export const ReprocessInboundEmailOutputSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

/**
 * Bulk Retrigger Email Rules Input Schema
 */
export const BulkRetriggerEmailRulesInputSchema = z
  .object({
    startDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for start of range"),
    endDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for end of range"),
    ruleIds: z
      .array(UUIDSchema)
      .optional()
      .describe(
        "Optional list of email rule IDs to retrigger. If not provided, all matching emails will be retriggered.",
      ),
  })
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    message: "endDate must be greater than or equal to startDate",
    path: ["endDate"],
  });

/**
 * Bulk Retrigger Email Rules Output Schema
 */
export const BulkRetriggerEmailRulesOutputSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  emailsQueued: z.number().describe("Number of emails queued for reprocessing"),
});

/**
 * Preview Bulk Retrigger Email Rules Input Schema
 */
export const PreviewBulkRetriggerEmailRulesInputSchema = z
  .object({
    startDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for start of range"),
    endDate: z
      .string()
      .datetime()
      .describe("ISO 8601 date string for end of range"),
    ruleIds: z
      .array(UUIDSchema)
      .optional()
      .describe(
        "Optional list of email rule IDs to filter by. If not provided, counts all emails in range.",
      ),
  })
  .refine((data) => new Date(data.endDate) >= new Date(data.startDate), {
    message: "endDate must be greater than or equal to startDate",
    path: ["endDate"],
  });

/**
 * Preview Bulk Retrigger Email Rules Output Schema
 */
export const PreviewBulkRetriggerEmailRulesOutputSchema = z.object({
  emailCount: z
    .number()
    .describe(
      "Number of emails that would be retriggered with current filters",
    ),
});

/**
 * Inbound Email Contracts
 */
export const TranslateAndSaveInboundEmailInputSchema = z.object({
  inboundEmailId: UUIDSchema,
  targetLanguage: z
    .string()
    .length(2)
    .default("en")
    .describe("ISO 639-1 language code"),
});

export const TranslateAndSaveInboundEmailOutputSchema = z.object({
  translatedContent: TranslatedEmailContentSchema,
  targetLanguage: z.string(),
});

export const inboundEmailContracts = {
  // Reprocess an inbound email
  reprocess: oc
    .input(ReprocessInboundEmailInputSchema)
    .output(ReprocessInboundEmailOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/reprocess",
    }),

  translateAndSave: oc
    .input(TranslateAndSaveInboundEmailInputSchema)
    .output(TranslateAndSaveInboundEmailOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/translate-and-save",
    }),

  // Bulk retrigger email rules for emails in a date range (admin only)
  bulkRetriggerEmailRules: oc
    .input(BulkRetriggerEmailRulesInputSchema)
    .output(BulkRetriggerEmailRulesOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/bulk-retrigger-email-rules",
    }),

  // Preview count of emails that would be retriggered (admin only)
  previewBulkRetriggerEmailRules: oc
    .input(PreviewBulkRetriggerEmailRulesInputSchema)
    .output(PreviewBulkRetriggerEmailRulesOutputSchema)
    .route({
      method: "POST",
      path: "/inbound-emails/preview-bulk-retrigger-email-rules",
    }),
} as const;

// Export types
export type ReprocessInboundEmailInput = z.infer<
  typeof ReprocessInboundEmailInputSchema
>;
export type ReprocessInboundEmailOutput = z.infer<
  typeof ReprocessInboundEmailOutputSchema
>;
export type BulkRetriggerEmailRulesInput = z.infer<
  typeof BulkRetriggerEmailRulesInputSchema
>;
export type BulkRetriggerEmailRulesOutput = z.infer<
  typeof BulkRetriggerEmailRulesOutputSchema
>;
export type PreviewBulkRetriggerEmailRulesInput = z.infer<
  typeof PreviewBulkRetriggerEmailRulesInputSchema
>;
export type PreviewBulkRetriggerEmailRulesOutput = z.infer<
  typeof PreviewBulkRetriggerEmailRulesOutputSchema
>;
export type TranslateAndSaveInboundEmailInput = z.infer<
  typeof TranslateAndSaveInboundEmailInputSchema
>;
export type TranslateAndSaveInboundEmailOutput = z.infer<
  typeof TranslateAndSaveInboundEmailOutputSchema
>;
